/**
 * AnimatedCard - Card with hover lift and optional spotlight border
 *
 * Features:
 * - Lift on hover (4px)
 * - Shadow increase
 * - Optional mouse-tracking spotlight border (Linear style)
 */

import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion'
import { type ReactNode, useRef } from 'react'
import { DURATION, EASE } from './motionVariants'
import { useReducedMotion } from './useReducedMotion'

interface AnimatedCardProps {
  children: ReactNode
  /** Enable mouse-tracking spotlight border */
  spotlight?: boolean
  /** Disable hover animations */
  disabled?: boolean
  /** Custom className */
  className?: string
  /** Custom styles */
  style?: React.CSSProperties
  /** Click handler */
  onClick?: () => void
}

export function AnimatedCard({
  children,
  spotlight = false,
  disabled = false,
  className,
  style,
  onClick,
}: AnimatedCardProps) {
  const prefersReducedMotion = useReducedMotion()
  const cardRef = useRef<HTMLDivElement>(null)

  // Mouse position for spotlight effect
  const mouseX = useMotionValue(0)
  const mouseY = useMotionValue(0)

  // Smooth mouse tracking
  const springConfig = { stiffness: 300, damping: 30 }
  const spotlightX = useSpring(mouseX, springConfig)
  const spotlightY = useSpring(mouseY, springConfig)

  // Transform mouse position to spotlight gradient position
  const spotlightBackground = useTransform(
    [spotlightX, spotlightY],
    ([x, y]) =>
      `radial-gradient(300px circle at ${x}px ${y}px, rgba(99, 102, 241, 0.06), transparent 70%)`
  )

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current || prefersReducedMotion || !spotlight) return

    const rect = cardRef.current.getBoundingClientRect()
    mouseX.set(e.clientX - rect.left)
    mouseY.set(e.clientY - rect.top)
  }

  const handleMouseLeave = () => {
    mouseX.set(0)
    mouseY.set(0)
  }

  const baseStyle: React.CSSProperties = {
    position: 'relative',
    background: '#ffffff',
    borderRadius: '16px',
    border: '1px solid #eaeaea',
    overflow: 'hidden',
    cursor: onClick ? 'pointer' : 'default',
    ...style,
  }

  if (disabled || prefersReducedMotion) {
    return (
      <div
        ref={cardRef}
        className={className}
        style={baseStyle}
        onClick={onClick}
      >
        {children}
      </div>
    )
  }

  return (
    <motion.div
      ref={cardRef}
      className={className}
      style={baseStyle}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onClick={onClick}
      initial={{ y: 0 }}
      whileHover={{
        y: -4,
        transition: {
          duration: DURATION.fast,
          ease: EASE.out,
        },
      }}
    >
      {/* Spotlight overlay */}
      {spotlight && (
        <motion.div
          style={{
            position: 'absolute',
            inset: 0,
            background: spotlightBackground,
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />
      )}

      {/* Content */}
      <div style={{ position: 'relative', zIndex: 1 }}>
        {children}
      </div>
    </motion.div>
  )
}

/**
 * GlowCard - Card with static glow effect on hover
 *
 * Simpler alternative to spotlight, uses border glow instead of mouse tracking.
 */
interface GlowCardProps {
  children: ReactNode
  className?: string
  style?: React.CSSProperties
  onClick?: () => void
}

export function GlowCard({
  children,
  className,
  style,
  onClick,
}: GlowCardProps) {
  const prefersReducedMotion = useReducedMotion()

  const baseStyle: React.CSSProperties = {
    position: 'relative',
    background: '#ffffff',
    borderRadius: '16px',
    border: '1px solid var(--line)',
    cursor: onClick ? 'pointer' : 'default',
    ...style,
  }

  if (prefersReducedMotion) {
    return (
      <div
        className={className}
        style={baseStyle}
        onClick={onClick}
      >
        {children}
      </div>
    )
  }

  return (
    <motion.div
      className={className}
      style={baseStyle}
      onClick={onClick}
      initial={{
        y: 0,
        boxShadow: '0 0 0 rgba(0, 0, 0, 0)',
        borderColor: '#eaeaea',
      }}
      whileHover={{
        y: -2,
        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.05)',
        borderColor: '#d4d4d4',
        transition: {
          duration: 0.18,
          ease: 'easeOut',
        },
      }}
    >
      {children}
    </motion.div>
  )
}
